// src/pages/App.tsx
import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar from "../ui/Sidebar";
import MobileTopbar from "../ui/MobileTopbar";
import ConversationsList from "../ui/ConversationsList";
import ConversationView from "../ui/ConversationView";
import DesktopConversationsLayout from "../ui/DesktopConversationsLayout";
import Knowledge from "../ui/Knowledge";
import Clients from "../ui/Clients";
import Profile from "../ui/Profile";
import ConversationTableAdmin from "../ui/ConversationTableAdmin";
import Signup from "./Signup";
import { useUser } from "../lib/UserContext";
import Dashboard from "../ui/Dashboard";
import ClinicInsights from "../ui/ClinicInsights";
import Calendars from "../ui/Calendars";
import { supabase } from "../lib/supabase";
import ContractGenerator from "../ui/Contract-generator";
import BillingAdmin from "../ui/BillingAdmin";
import BillingFollowUp from "../ui/BillingFollowUp";
import CRM from "../ui/CRM";
import SelectActiveDoctor from "../components/SelectActiveDoctor";
import NewMessage from "./NewMessage";
import Reminders from "./Reminders";
import Templates from "./Templates";
import Settings from "./Settings";
import AdminEmail from "./AdminEmail";
import PlatoScraper from "../ui/PlatoScraper";
import TokenUsage from "../ui/TokenUsage";
import QuickMessageDiscovery from "../ui/QuickMessageDiscovery";
import PdpaRegister from "../ui/PdpaRegister";
import MissionControl from "../ui/MissionControl";
import { usePatientDirectory } from "../lib/PatientDirectoryContext";
import PatientDirectory from "../ui/PatientDirectory";
import StaffPicker from "../components/StaffPicker";
import { useStaff } from "../lib/StaffContext";

export default function App() {
  const user = useUser();
  const location = useLocation();
  const { setActivePractitionerId, loadDirectory } = usePatientDirectory();
  const { activeStaff } = useStaff();
  const [isDesktop, setIsDesktop] = useState(() =>
    window.matchMedia("(min-width: 1024px)").matches
  );

  const [activeDoctor, setActiveDoctor] = useState(() => {
    const stored = localStorage.getItem("activeDoctor");
    return stored ? JSON.parse(stored) : null;
  });
  const [hasPractitioners, setHasPractitioners] = useState<boolean | null>(null);
  const [enableDesktopSplitView, setEnableDesktopSplitView] = useState(false);
  const [enableExperienceIntelligence, setEnableExperienceIntelligence] = useState(false);
  const [enableQuickMessages, setEnableQuickMessages] = useState(false);
  const shouldUseDesktopSplitView = enableDesktopSplitView && isDesktop;
  const isDesktopConversationsPage =
    shouldUseDesktopSplitView && location.pathname === "/conversations";

  useEffect(() => {
    if (!user) return;
  
    // user.id = clinic_id dans ton backoffice (cas admin/praticien)
    supabase
      .from("clients")
      .select("enable_desktop_split_view, enable_experience_intelligence, enable_quick_messages")
      .eq("id", user.id)
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error("[DESKTOP SPLIT FLAG] error:", error);
          return;
        }
        setEnableDesktopSplitView(data?.enable_desktop_split_view === true);
        setEnableExperienceIntelligence(data?.enable_experience_intelligence === true);
        setEnableQuickMessages(data?.enable_quick_messages === true);
      });
  }, [user]);

  useEffect(() => {
    const targetId = user?.role === "admin" ? activeDoctor?.id : user?.id;
    setActivePractitionerId(targetId || null);
    if (targetId) loadDirectory(targetId);
  }, [
    activeDoctor?.id,
    loadDirectory,
    setActivePractitionerId,
    user?.id,
    user?.role,
  ]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(media.matches);
    if (media.addEventListener) {
      media.addEventListener("change", handleChange);
    } else {
      media.addListener(handleChange);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", handleChange);
      } else {
        media.removeListener(handleChange);
      }
    };
  }, []);

  // Vérifie combien de praticiens existent pour la clinique connectée
  useEffect(() => {
    async function checkPractitioners() {
      if (!user) return;
      const { data, error } = await supabase
        .from("clinic_practitioners")
        .select("id")
        .eq("clinic_id", user.id);

      if (error) console.error("Error checking practitioners:", error);
      setHasPractitioners((data?.length || 0) > 0);
    }
    checkPractitioners();
  }, [user]);

  /* 🧩 Étape 1 : Inscription aux notifications push --------------------- */
  useEffect(() => {
    if (!user) return;

    async function subscribe() {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        console.warn("Push non supporté par ce navigateur");
        return;
      }

      try {
        // Récupère la clé publique VAPID depuis le backend
        const res = await fetch(`${import.meta.env.VITE_API_URL}/push/vapid-public`);
        const { publicKey } = await res.json();

        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: publicKey,
        });

        const { endpoint, keys } = sub.toJSON();
        const raw = (key: string) => key || "";

        // On upsert uniquement avec l'id de l'utilisateur authentifié pour
        // respecter la RLS policy (auth.uid() = praticien_id).
        // Utiliser activeDoctor.id causerait des erreurs 403 quand l'admin
        // visualise un autre praticien.
        const targetId = user.id;

        const { error } = await supabase.from("push_subscriptions").upsert(
          {
            praticien_id: targetId,
            endpoint,
            p256dh: raw(keys?.p256dh),
            auth: raw(keys?.auth),
            user_agent: navigator.userAgent,
          },
          {
            onConflict: "endpoint",
            returning: "representation",
          }
        );

        if (error) {
          console.error("Erreur sauvegarde push_sub:", error.message);
        } else {
          console.log("✅ Abonnement push enregistré pour:", targetId);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : JSON.stringify(err);
        console.error("Erreur abonnement push:", message);
      }
    }

    // Abonnement push (auto pour tous, compatible anciens et nouveaux comptes)
    subscribe();
  }, [user, activeDoctor]);

  /* 🕒 Étape 2 : États de session -------------------------------------- */

  // ⏳ En cours de restauration de session (user === undefined)
  if (user === undefined) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-700 bg-white">
        Loading, please wait a bit…
      </div>
    );
  }

  // 🚫 Non authentifié (user === null)
  if (user === null) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  /* 👤 Étape 3 : Utilisateur connecté --------------------------------- */
  if (hasPractitioners === null) {
    // On attend la vérification de la table clinic_practitioners
    return (
      <div className="flex h-screen items-center justify-center text-gray-600">
        Checking practitioners…
      </div>
    );
  }

  // Level 3: RBAC mode — show staff picker if no active staff session
  if (user.rbac_enabled && user.role === "praticien" && !activeStaff) {
    return <StaffPicker />;
  }

  // Cas 1️⃣ — aucune ligne dans clinic_practitioners → accès direct à l'app
  if (hasPractitioners === false) {
    return (
      <div className="flex h-screen bg-softgray text-slate-800 overflow-hidden">
        <Sidebar
          enableExperienceIntelligence={enableExperienceIntelligence}
        />
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <MobileTopbar />
          <main
            className={
              isDesktopConversationsPage
                ? "flex flex-col flex-1 min-h-0 overflow-hidden"
                : "flex-1 overflow-y-auto"
            }
          >
          <div className={isDesktopConversationsPage ? "contents" : "px-6 py-8 max-w-6xl mx-auto w-full"}>
          <Routes>
            {/* Redir / → /conversations */}
            <Route path="/" element={<Navigate to="/conversations" replace />} />

            {/* Conversations */}
            <Route
              path="/conversations"
              element={
                shouldUseDesktopSplitView ? (
                  <DesktopConversationsLayout activeDoctor={activeDoctor} />
                ) : (
                  <ConversationsList activeDoctor={activeDoctor} />
                )
              }
            />
            <Route
              path="/conversations/:id"
              element={
                shouldUseDesktopSplitView ? (
                  <Navigate to="/conversations" replace />
                ) : (
                  <ConversationView />
                )
              }
            />

            {/* Autres pages accessibles à tous les praticiens */}
            <Route path="/knowledge" element={<Knowledge />} />
            {user.role === "admin" ? (
              <Route path="/profile" element={<Profile />} />
            ) : (
              <>
                <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />
                <Route path="/templates" element={<Navigate to="/settings/templates" replace />} />
                <Route path="/team" element={<Navigate to="/settings/team" replace />} />
                <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
                <Route
                  path="/settings/:tab"
                  element={<Settings enableQuickMessages={enableQuickMessages} />}
                />
              </>
            )}
            <Route path="/new-message" element={<NewMessage />} />
            <Route path="/reminders" element={<Reminders />} />
            <Route path="/dashboard" element={<Dashboard />} />
            {enableExperienceIntelligence && (
              <Route path="/clinic-insights" element={<ClinicInsights />} />
            )}
            <Route path="/billing" element={<BillingAdmin />} />
            <Route path="/patients" element={<PatientDirectory />} />

            {/* Admin uniquement */}
            {user.role === "admin" && (
              <>
                <Route path="/admin/mission-control" element={<MissionControl />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/conversations-admin" element={<ConversationTableAdmin />} />
                <Route path="/calendars" element={<Calendars />} />
                <Route path="/contract-generator" element={<ContractGenerator />} />
                <Route path="/billing-follow-up" element={<BillingFollowUp />} />
                <Route path="/crm" element={<CRM />} />
                <Route path="/admin/plato" element={<PlatoScraper />} />
                <Route path="/admin/token-usage" element={<TokenUsage />} />
                <Route path="/admin/quick-message-discovery" element={<QuickMessageDiscovery />} />
                <Route path="/admin/templates" element={<Templates />} />
                <Route path="/admin/email" element={<AdminEmail />} />
                <Route path="/admin/pdpa-register" element={<PdpaRegister />} />
              </>
            )}

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/conversations" replace />} />
          </Routes>
          </div>
          </main>
        </div>
      </div>
    );
  }

  // Cas 2️⃣ — des praticiens existent mais aucun n’a encore été choisi (Level 2 seulement)
  // Level 3 (rbac_enabled) bypasse SelectActiveDoctor — le staff sub-session suffit
  if (hasPractitioners === true && !activeDoctor && !user.rbac_enabled) {
    return (
      <SelectActiveDoctor
        clinicId={user.id}
        onSelect={(doctor) => setActiveDoctor(doctor)}
      />
    );
  }

  // Cas 3️⃣ — un praticien est déjà sélectionné → affichage normal de l’app
  return (
    <div className="flex h-screen bg-softgray text-slate-800 overflow-hidden">
      <Sidebar
        activeDoctor={activeDoctor}
        enableExperienceIntelligence={enableExperienceIntelligence}
      />
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <MobileTopbar activeDoctor={activeDoctor} />
        <main
          className={
            isDesktopConversationsPage
              ? "flex flex-col flex-1 min-h-0 overflow-hidden"
              : "flex-1 overflow-y-auto"
          }
        >
        <div className={isDesktopConversationsPage ? "contents" : "px-6 py-8 max-w-6xl mx-auto w-full"}>
        <Routes>
          {/* Redir / → /conversations */}
          <Route path="/" element={<Navigate to="/conversations" replace />} />

          {/* Conversations */}
          <Route
            path="/conversations"
            element={
              shouldUseDesktopSplitView ? (
                <DesktopConversationsLayout activeDoctor={activeDoctor} />
              ) : (
                <ConversationsList activeDoctor={activeDoctor} />
              )
            }
          />
          <Route
            path="/conversations/:id"
            element={
              shouldUseDesktopSplitView ? (
                <Navigate to="/conversations" replace />
              ) : (
                <ConversationView />
              )
            }
          />

          {/* Autres pages accessibles à tous les praticiens */}
          <Route path="/knowledge" element={<Knowledge />} />
          {user.role === "admin" ? (
            <Route path="/profile" element={<Profile />} />
          ) : (
            <>
              <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />
              <Route path="/templates" element={<Navigate to="/settings/templates" replace />} />
              <Route path="/team" element={<Navigate to="/settings/team" replace />} />
              <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
              <Route
                path="/settings/:tab"
                element={<Settings enableQuickMessages={enableQuickMessages} />}
              />
            </>
          )}
          <Route path="/new-message" element={<NewMessage />} />
          <Route path="/reminders" element={<Reminders />} />
          <Route path="/dashboard" element={<Dashboard />} />
          {enableExperienceIntelligence && (
            <Route path="/clinic-insights" element={<ClinicInsights />} />
          )}
          <Route path="/billing" element={<BillingAdmin />} />
          <Route
            path="/patients"
            element={<PatientDirectory activeDoctor={activeDoctor} />}
          />

          {/* Admin uniquement */}
          {user.role === "admin" && (
            <>
              <Route path="/admin/mission-control" element={<MissionControl />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/conversations-admin" element={<ConversationTableAdmin />} />
              <Route path="/calendars" element={<Calendars />} />
              <Route path="/contract-generator" element={<ContractGenerator />} />
              <Route path="/billing-follow-up" element={<BillingFollowUp />} />
              <Route path="/crm" element={<CRM />} />
              <Route path="/admin/plato" element={<PlatoScraper />} />
              <Route path="/admin/token-usage" element={<TokenUsage />} />
              <Route path="/admin/quick-message-discovery" element={<QuickMessageDiscovery />} />
              <Route path="/admin/templates" element={<Templates />} />
              <Route path="/admin/email" element={<AdminEmail />} />
              <Route path="/admin/pdpa-register" element={<PdpaRegister />} />
            </>
          )}

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/conversations" replace />} />
        </Routes>
        </div>
        </main>
      </div>
    </div>
  );
}
